import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Input, Spin, Typography } from "antd";
import {
  AndroidOutlined,
  AppleOutlined,
  CalendarOutlined,
  CopyOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  FacebookOutlined,
  FileTextOutlined,
  GlobalOutlined,
  IdcardOutlined,
  LockOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  ShoppingOutlined,
  ShareAltOutlined,
  TwitterOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./QRPublic.css";

const { Title, Paragraph } = Typography;

const parsePayload = (payloadData) => {
  if (!payloadData) {
    return {};
  }

  try {
    return typeof payloadData === "string" ? JSON.parse(payloadData) : payloadData;
  } catch {
    return {};
  }
};

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

const sanitizeHtml = (html = "") =>
  html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");

const getDeviceType = () => (/Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop");

const getBrowserName = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Edg")) return "Edge";
  return "Unknown";
};

const getSocialInitial = (platform = "S") => platform.trim().charAt(0).toUpperCase() || "S";

const PRODUCT_PLATFORMS = ["Amazon", "Flipkart", "Myntra", "Shopify", "Meesho", "Custom Store"];

const getProductPlatform = (platform = "") =>
  PRODUCT_PLATFORMS.includes(platform) ? platform : "";

const getFileName = (url = "") => {
  try {
    return decodeURIComponent(url.split("/").pop()?.split("?")[0] || "File");
  } catch {
    return "File";
  }
};

const getFileExtension = (url = "") => {
  const fileName = getFileName(url);
  return fileName.includes(".") ? fileName.split(".").pop().toUpperCase() : "FILE";
};

const formatDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isImageFile = (url = "") => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url.split("?")[0]);

const getFirstUrl = (...values) => {
  for (const value of values) {
    if (!value) continue;
    if (Array.isArray(value)) {
      const match = value
        .map((item) => getFirstUrl(item))
        .find(Boolean);
      if (match) return match;
      continue;
    }
    if (typeof value === "object") {
      const match =
        value.fileUrl ||
        value.url ||
        value.imageUrl ||
        value.pictureUrl ||
        value.attachmentUrl ||
        value.messageImageUrl ||
        value.photoUrl ||
        value.s3Url ||
        value.file?.fileUrl ||
        value.file?.url ||
        value.image?.fileUrl ||
        value.image?.url ||
        value.picture?.fileUrl ||
        value.picture?.url ||
        "";
      if (match) return match;
      continue;
    }
    return value;
  }
  return "";
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "VC";
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
};

const collectDynamicCoupons = (data = {}) => {
  const sources = [
    data.couponMappings,
    data.coupons,
    data.mappedCoupons,
    data.batchCoupons,
    data.couponBatch?.coupons,
    data.couponBatch?.data,
    data.batch?.coupons,
    data.batch?.data,
  ];

  const collected = [];

  const collect = (value) => {
    if (!value) return;

    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }

    if (typeof value !== "object") return;

    const nested = value.coupons || value.mappedCoupons || value.batchCoupons || value.couponBatch?.coupons || value.batch?.coupons;
    if (nested) {
      collect(nested);
    }

    if (value.couponCode || value.code || value.couponName || value.serviceLabel) {
      collected.push(value);
    }
  };

  sources.forEach(collect);

  const seen = new Set();
  return collected.filter((coupon, index) => {
    const key = coupon.id || coupon.couponId || coupon.couponCode || coupon.code || index;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getCouponVisual = (coupon = {}) => {
  const text = `${coupon.couponName || ""} ${coupon.serviceLabel || ""}`.toLowerCase();
  if (text.includes("mop")) return "🧹";
  if (text.includes("dust")) return "🪣";
  if (text.includes("clean") || text.includes("home")) return "🏠";
  if (text.includes("iron") || text.includes("cloth")) return "👔";
  if (text.includes("order") || text.includes("off") || text.includes("discount")) return "💰";
  return "🎟️";
};

const isCouponUsable = (coupon = {}) => {
  const status = String(coupon.status || "Active").toLowerCase();
  const remainingUses = coupon.remainingUses ?? ((coupon.usageLimit ?? 0) - (coupon.usedCount ?? 0));
  const now = new Date();
  const startsAt = coupon.startDate ? new Date(coupon.startDate) : null;
  const expiresAt = coupon.expiryDate ? new Date(coupon.expiryDate) : null;

  if (status !== "active") return false;
  if (Number.isFinite(Number(remainingUses)) && Number(remainingUses) <= 0) return false;
  if (startsAt && !Number.isNaN(startsAt.getTime()) && startsAt > now) return false;
  if (expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt < now) return false;
  return true;
};

const getDynamicLandingPage = (data = {}) => {
  const landingPage = data.landingPage || {};
  const usageInstructions = Array.isArray(data.usageInstructions)
    ? data.usageInstructions
    : Array.isArray(landingPage.usageInstructions)
      ? landingPage.usageInstructions
      : [];
  const extraLinks = Array.isArray(data.extraLinks)
    ? data.extraLinks
    : Array.isArray(landingPage.extraLinks)
      ? landingPage.extraLinks
      : [];

  return {
    appPlayStoreLink: data.appPlayStoreLink || landingPage.appPlayStoreLink || "",
    appAppleAppStoreLink: data.appAppleAppStoreLink || landingPage.appAppleAppStoreLink || "",
    websiteLink: data.websiteLink || landingPage.websiteLink || "",
    heading: data.heading || landingPage.heading || "",
    subheading: data.subheading || landingPage.subheading || "",
    usageInstructions: usageInstructions.map((item) => ({
      title: String(item?.title || ""),
      description: String(item?.description || ""),
    })),
    extraLinks: extraLinks.map((item) => ({
      label: String(item?.label || ""),
      value: String(item?.value || ""),
      type: String(item?.type || ""),
    })),
    headingLogoUrl: data.headingLogoUrl || landingPage.headingLogoUrl || "",
    headingCardBackground: data.headingCardBackground || landingPage.headingCardBackground || "",
    headingTextColor: data.headingTextColor || landingPage.headingTextColor || "",
    subheadingTextColor: data.subheadingTextColor || landingPage.subheadingTextColor || "",
  };
};

const hasDynamicLandingPage = (landing = {}) =>
  Boolean(
    landing.appPlayStoreLink ||
    landing.appAppleAppStoreLink ||
    landing.websiteLink ||
    landing.heading ||
    landing.subheading ||
    landing.usageInstructions?.length > 0 ||
    landing.extraLinks?.length > 0 ||
    landing.headingLogoUrl
  );

const getDynamicExtraLinkIcon = (type = "") => {
  const normalized = type.toLowerCase();
  if (normalized === "youtube") return <YoutubeOutlined />;
  if (normalized === "facebook") return <FacebookOutlined />;
  if (normalized === "twitter" || normalized === "x") return <TwitterOutlined />;
  if (normalized === "website") return <GlobalOutlined />;
  return <ShareAltOutlined />;
};

const QRPublic = () => {
  const { uniqueId } = useParams();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [qrData, setQrData] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [copiedCouponCode, setCopiedCouponCode] = useState("");
  const trackedRef = useRef(false);

  const fetchDynamicQR = useCallback(async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BASE_URL}/api/DynamicQr/public/${uniqueId}/scan`);
    } catch {
      // Dynamic QR scan tracking must not block open resolution.
    }

    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}/api/DynamicQr/public/${uniqueId}/open`
    );

    if (response.data?.error) {
      throw new Error(response.data?.message || "Unable to load this dynamic QR code.");
    }

    return {
      ...(response.data?.data || {}),
      isDynamicQr: true,
    };
  }, [uniqueId]);

  const fetchQR = useCallback(async () => {
    setLoading(true);
    setError("");
    setPasswordError("");
    trackedRef.current = false;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/WebsiteQr/${uniqueId}`
      );

      if (!response.data?.error) {
        setQrData(response.data.data || {});
      } else {
        setError(response.data?.message || "Unable to load this QR code.");
      }
    } catch (err) {
      try {
        setQrData(await fetchDynamicQR());
      } catch (dynamicError) {
        setError(dynamicError?.response?.data?.message || dynamicError?.message || err?.response?.data?.message || "This QR code could not be found.");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchDynamicQR, uniqueId]);

  const trackScan = useCallback(async () => {
    if (trackedRef.current) return;
    trackedRef.current = true;

    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/Qr/${uniqueId}/track-scan`, {
        device: getDeviceType(),
        browser: getBrowserName(),
        location: "",
      });
      if (!response.data?.error && response.data?.data) {
        setQrData((prev) => ({ ...(prev || {}), ...response.data.data }));
      }
    } catch {
      // Scan tracking must never block content rendering.
    }
  }, [uniqueId]);

  useEffect(() => {
    fetchQR();
  }, [fetchQR]);

  useEffect(() => {
    if (qrData && !qrData.isPasswordProtected && !qrData.isDynamicQr) {
      trackScan();
    }
  }, [qrData, trackScan]);

  const verifyPassword = async () => {
    if (!password.trim()) {
      setPasswordError("Please enter the QR password.");
      return;
    }

    setVerifying(true);
    setPasswordError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/WebsiteQr/${uniqueId}/verify-password`,
        { password }
      );

      if (!response.data?.error) {
        setQrData((prev) => ({ ...(prev || {}), ...(response.data.data || {}) }));
        setPassword("");
        trackedRef.current = false;
        await trackScan();
      } else {
        setPasswordError(response.data?.message || "Invalid QR password.");
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        setPasswordError("Invalid QR password.");
      } else {
        setPasswordError(err?.response?.data?.message || "Unable to verify password.");
      }
    } finally {
      setVerifying(false);
    }
  };

  const normalizeExternalUrl = (url = "") => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return "";
    if (/^(https?:|mailto:|tel:)/i.test(trimmedUrl)) return trimmedUrl;
    return `https://${trimmedUrl}`;
  };

  const openUrl = (url) => {
    if (!url) return;
    window.open(normalizeExternalUrl(url), "_blank", "noopener,noreferrer");
  };

  const trackDynamicCouponAction = async (action) => {
    try {
      await axios.post(`${process.env.REACT_APP_BASE_URL}/api/DynamicQr/public/${uniqueId}/${action}`);
    } catch {
      // Analytics tracking should not interrupt the customer action.
    }
  };

  const copyCouponCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCouponCode(code);
      await trackDynamicCouponAction("coupon-copied");
      window.setTimeout(() => {
        setCopiedCouponCode((current) => current === code ? "" : current);
      }, 1800);
    } catch {
      // Clipboard support varies by browser.
    }
  };

  const renderDynamicInstructions = (landing = {}) => {
    const instructions = Array.isArray(landing.usageInstructions)
      ? landing.usageInstructions.filter((item) => item.title || item.description)
      : [];
    if (instructions.length === 0) return null;

    return (
      <div className="qrp-dynamic-instructions">
        <span>How to use</span>
        <div className="qrp-dynamic-instruction-list">
          {instructions.map((instruction, index) => (
            <div className="qrp-dynamic-instruction-item" key={`${instruction.title}-${index}`}>
              <strong>{instruction.title}</strong>
              <p>{instruction.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDynamicLinks = (landing = {}, { showPrimary = true, showExtra = true } = {}) => {
    const extraLinks = Array.isArray(landing.extraLinks)
      ? landing.extraLinks.filter((item) => item.label && item.value && item.type)
      : [];
    const hasPrimaryLinks = landing.websiteLink || landing.appPlayStoreLink || landing.appAppleAppStoreLink;
    const hasLinks = (showPrimary && hasPrimaryLinks) || (showExtra && extraLinks.length > 0);

    if (!hasLinks) return null;

    return (
      <div className="qrp-dynamic-links">
        {showPrimary && landing.websiteLink && (
          <Button icon={<GlobalOutlined />} onClick={() => openUrl(landing.websiteLink)}>
            Website
          </Button>
        )}
        {showPrimary && landing.appPlayStoreLink && (
          <Button icon={<AndroidOutlined />} onClick={() => openUrl(landing.appPlayStoreLink)}>
            Play Store
          </Button>
        )}
        {showPrimary && landing.appAppleAppStoreLink && (
          <Button icon={<AppleOutlined />} onClick={() => openUrl(landing.appAppleAppStoreLink)}>
            App Store
          </Button>
        )}
        {showExtra && extraLinks.map((link, index) => (
          <Button
            icon={getDynamicExtraLinkIcon(link.type)}
            key={`${link.type}-${link.value}-${index}`}
            onClick={() => openUrl(link.value)}
          >
            {link.label}
          </Button>
        ))}
      </div>
    );
  };

  const renderDynamicLandingPage = (
    landing = {},
    { showInstructions = true, showLinks = true, showPrimaryLinks = true, showExtraLinks = true } = {}
  ) => {
    const backgroundColor = landing.headingCardBackground || "#F5F7FB";
    const headingTextColor = landing.headingTextColor || "#111827";
    const subheadingTextColor = landing.subheadingTextColor || "#6B7280";
    const links = renderDynamicLinks(landing, { showPrimary: showPrimaryLinks, showExtra: showExtraLinks });
    const hasHeading = landing.heading || landing.subheading;
    const instructions = renderDynamicInstructions(landing);

    if (!hasHeading && !(showLinks && links) && !(showInstructions && instructions)) return null;

    return (
      <div className="qrp-dynamic-landing">
        {hasHeading && (
          <div className="qrp-dynamic-heading-card" style={{ backgroundColor }}>
            <div>
              {landing.heading && <h2 style={{ color: headingTextColor }}>{landing.heading}</h2>}
              {landing.subheading && <p style={{ color: subheadingTextColor }}>{landing.subheading}</p>}
            </div>
          </div>
        )}
        {showInstructions && instructions}
        {showLinks && links}
      </div>
    );
  };

  const renderContent = () => {
    if (qrData?.isDynamicQr) {
      const landing = getDynamicLandingPage(qrData);
      if (!qrData.isActionAllowed) {
        return (
          <div className="qrp-dynamic qrp-dynamic-state">
            <div className="qrp-dynamic-hero unavailable">
              <div className="qrp-dynamic-badge"><MessageOutlined /></div>
            </div>
            <div className="qrp-dynamic-body">
              <span className="qrp-type-pill">Dynamic QR</span>
              <Title level={2}>QR unavailable</Title>
              <Paragraph>{qrData.message || "This QR code is not available."}</Paragraph>
              {(qrData.scanLimit || qrData.remainingScans !== undefined || qrData.isScanLimitReached) && (
                <div className="qrp-dynamic-url">
                  <MessageOutlined />
                  <div>
                    <strong>Scan limit</strong>
                    <span>
                      {qrData.scanLimit ? `${qrData.scanCount || 0} / ${qrData.scanLimit} scans used` : "Unlimited scans"}
                      {qrData.remainingScans !== null && qrData.remainingScans !== undefined ? ` · ${qrData.remainingScans} remaining` : ""}
                    </span>
                  </div>
                </div>
              )}
              <Button icon={<ReloadOutlined />} onClick={fetchQR}>Try Again</Button>
            </div>
          </div>
        );
      }

      if (qrData.qrOption === "Link") {
        const linkUrl = qrData.linkUrl || "";
        const displayHost = (() => {
          try {
            return new URL(normalizeExternalUrl(linkUrl)).hostname.replace(/^www\./, "");
          } catch {
            return linkUrl || "Website link";
          }
        })();

        return (
          <div className="qrp-dynamic qrp-dynamic-link">
            <div className="qrp-dynamic-hero link">
              <div className="qrp-dynamic-badge"><GlobalOutlined /></div>
              <div className="qrp-dynamic-scan-lines">
                <span />
                <span />
                <span />
              </div>
            </div>
            <div className="qrp-dynamic-body">
              <span className="qrp-type-pill">Dynamic Link QR</span>
              {hasDynamicLandingPage(landing) ? (
                renderDynamicLandingPage(landing)
              ) : (
                <>
                  <Title level={2}>Continue to Website</Title>
                  <Paragraph className="qrp-dynamic-subtitle">This QR destination can be updated by the business anytime.</Paragraph>
                </>
              )}
              <div className="qrp-dynamic-url">
                <GlobalOutlined />
                <div>
                  <strong>{displayHost}</strong>
                  <span>{normalizeExternalUrl(linkUrl)}</span>
                </div>
              </div>
              <Button type="primary" size="large" block onClick={() => openUrl(linkUrl)} disabled={!linkUrl}>
                Open Link
              </Button>
            </div>
          </div>
        );
      }

      if (qrData.qrOption === "CouponMapping") {
        const coupons = collectDynamicCoupons(qrData);
        return (
          <div className="qrp-dynamic qrp-dynamic-coupons">
            <div className="qrp-dynamic-hero coupon">
              <div className={`qrp-dynamic-badge ${landing.headingLogoUrl ? "has-logo" : ""}`}>
                {landing.headingLogoUrl ? (
                  <img src={landing.headingLogoUrl} alt={landing.heading || "Dynamic QR"} />
                ) : (
                  <SafetyCertificateOutlined />
                )}
              </div>
            </div>
            <div className="qrp-dynamic-body">
              {hasDynamicLandingPage(landing) ? (
                renderDynamicLandingPage(landing, { showInstructions: false, showExtraLinks: false })
              ) : (
                <>
                  <Title level={2}>Available Offers</Title>
                  <Paragraph className="qrp-dynamic-subtitle">Copy a coupon code and use it with the business offer.</Paragraph>
                </>
              )}
              <div className="qrp-coupon-list">
                {coupons.length === 0 ? (
                  <div className="qrp-empty-coupon">
                    <FileTextOutlined />
                    <Paragraph>No coupons are currently mapped to this QR.</Paragraph>
                  </div>
                ) : coupons.map((coupon, index) => {
                  const code = coupon.couponCode || coupon.code || "";
                  const isCopied = copiedCouponCode === code;
                  const isDisabled = !isCouponUsable(coupon);
                  const couponTitle = coupon.couponName || coupon.title || coupon.serviceLabel || "Mapped offer";
                  const notesTerms = String(coupon.notesTerms || "").trim();
                  return (
                    <div className={`qrp-coupon-item ${isCopied ? "copied" : ""} ${isDisabled ? "disabled" : ""}`} key={coupon.id || code || index}>
                      <div className="qrp-coupon-top">
                        <div className="qrp-coupon-main">
                          <div className="qrp-coupon-visual">{getCouponVisual(coupon)}</div>
                          <div>
                            <span>{couponTitle}</span>
                            <strong>{code || "Coupon"}</strong>
                            {isDisabled ? (
                              <small>Currently unavailable</small>
                            ) : coupon.expiryDate ? (
                              <small>Valid till {formatDate(coupon.expiryDate)}</small>
                            ) : null}
                          </div>
                        </div>
                        <Button
                          icon={<CopyOutlined />}
                          type={isCopied ? "primary" : "default"}
                          disabled={isDisabled}
                          onClick={async () => {
                            if (isDisabled) return;
                            await copyCouponCode(code);
                          }}
                        >
                          {isCopied ? "Copied" : "Tap to Copy"}
                        </Button>
                      </div>
                      {notesTerms && (
                        <div className="qrp-coupon-notes">
                          <p>{notesTerms}</p>
                        </div>
                      )}
                      {isCopied && (
                        <div className="qrp-coupon-copied-note">
                          "{code}" copied to clipboard! Paste it when signing up.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {renderDynamicLinks(landing, { showPrimary: false, showExtra: true })}
              {renderDynamicInstructions(landing)}
            </div>
          </div>
        );
      }

      return (
        <div className="qrp-dynamic qrp-dynamic-state">
          <div className="qrp-dynamic-hero blank">
            <div className="qrp-dynamic-badge"><MessageOutlined /></div>
          </div>
          <div className="qrp-dynamic-body">
            <span className="qrp-type-pill">Blank Dynamic QR</span>
            {hasDynamicLandingPage(landing) ? (
              renderDynamicLandingPage(landing)
            ) : (
              <>
                <Title level={2}>Not configured yet</Title>
                <Paragraph>{qrData.message || "This QR code is ready, but no action has been assigned yet."}</Paragraph>
              </>
            )}
          </div>
        </div>
      );
    }

    const payload = parsePayload(qrData?.payloadData);
    const qrType = qrData?.qrType;
    const link = qrData?.link || payload.url || payload.websiteUrl || payload.mapsUrl || payload.socialUrl || payload.productUrl;
    const text = qrData?.qrText || payload.text || payload.description || "";

    if (qrType === "Location") {
      const venue = payload.venueName || qrData?.title || "Location";
      const address = payload.address || "Open this location in maps";

      return (
        <div className="qrp-location">
          <div className="qrp-location-hero">
            <div className="qrp-location-pin"><EnvironmentOutlined /></div>
            <div className="qrp-location-map-lines" />
          </div>
          <div className="qrp-location-body">
            <span className="qrp-type-pill">Location QR</span>
            <Title level={2}>{venue}</Title>
            <Paragraph className="qrp-location-address">{address}</Paragraph>
            <Button type="primary" size="large" onClick={() => openUrl(link)} disabled={!link}>
              Open Google Maps
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Brand") {
      const companyName = payload.companyName || qrData?.title || "Brand";
      const website = qrData?.link || payload.websiteUrl || payload.url || link;

      return (
        <div className="qrp-brand">
          <div className="qrp-brand-hero">
            <div className="qrp-brand-glow" />
            <div className="qrp-brand-logo">
              {qrData.logoUrl ? (
                <img src={qrData.logoUrl} alt={companyName} />
              ) : (
                <GlobalOutlined />
              )}
            </div>
          </div>
          <div className="qrp-brand-body">
            <span className="qrp-type-pill">Brand QR</span>
            <Title level={2}>{companyName}</Title>
            <Paragraph className="qrp-brand-subtitle">{qrData?.title}</Paragraph>
            {website && <div className="qrp-brand-url"><GlobalOutlined /> {website}</div>}
            <Button type="primary" size="large" onClick={() => openUrl(website)} disabled={!website}>
              Visit Website
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Social") {
      const platform = payload.platform || "Social";
      const companyName = payload.companyName || qrData?.title || platform;
      const socialUrl = qrData?.link || payload.socialUrl || payload.url || link;

      return (
        <div className="qrp-social">
          <div className="qrp-social-hero">
            <div className="qrp-social-orbit one" />
            <div className="qrp-social-orbit two" />
            <div className="qrp-social-avatar">{getSocialInitial(platform)}</div>
          </div>
          <div className="qrp-social-body">
            <span className="qrp-type-pill">Social QR</span>
            <Title level={2}>{companyName}</Title>
            <div className="qrp-social-platform"><ShareAltOutlined /> {platform}</div>
            {socialUrl && <Paragraph className="qrp-social-url">{socialUrl}</Paragraph>}
            <Button type="primary" size="large" onClick={() => openUrl(socialUrl)} disabled={!socialUrl}>
              Open {platform}
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Product") {
      const productName = qrData?.title || "Product";
      const companyName = payload.companyName || "";
      const platform = getProductPlatform(payload.platform);
      const sku = payload.sku || "";
      const productUrl = qrData?.link || payload.productUrl || payload.url || link;

      return (
        <div className="qrp-product">
          <div className="qrp-product-hero">
            <div className="qrp-product-bag"><ShoppingOutlined /></div>
          </div>
          <div className="qrp-product-body">
            <span className="qrp-type-pill">Product QR</span>
            <Title level={2}>{productName}</Title>
            {companyName && <Paragraph className="qrp-product-company">{companyName}</Paragraph>}
            {(platform || sku) && (
              <div className="qrp-product-chips">
                {platform && <span>{platform}</span>}
                {sku && <span>SKU: {sku}</span>}
              </div>
            )}
            {productUrl && <div className="qrp-product-url"><GlobalOutlined /> {productUrl}</div>}
            <Button type="primary" size="large" onClick={() => openUrl(productUrl)} disabled={!productUrl}>
              View Product
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Website") {
      const websiteTitle = qrData?.title || payload.title || "Website";
      const websiteUrl = qrData?.link || payload.url || payload.link || payload.websiteUrl || link;
      const hostname = (() => {
        try {
          return new URL(websiteUrl).hostname.replace(/^www\./, "");
        } catch {
          return websiteUrl || "Website link";
        }
      })();

      return (
        <div className="qrp-website">
          <div className="qrp-website-hero">
            <div className="qrp-website-ring" />
            <div className="qrp-website-icon"><GlobalOutlined /></div>
          </div>
          <div className="qrp-website-body">
            <span className="qrp-type-pill">Website QR</span>
            <Title level={2}>{websiteTitle}</Title>
            <Paragraph className="qrp-website-domain">{hostname}</Paragraph>
            {websiteUrl && (
              <div className="qrp-website-url">
                <GlobalOutlined /> {normalizeExternalUrl(websiteUrl)}
              </div>
            )}
            <Button type="primary" size="large" onClick={() => openUrl(websiteUrl)} disabled={!websiteUrl}>
              Open Website
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "Text") {
      const textTitle = qrData?.title || payload.title || "Text";
      const textContent = text || payload.text || "No content available.";

      return (
        <div className="qrp-text-card">
          <div className="qrp-text-hero">
            <div className="qrp-text-paper">
              <FileTextOutlined />
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className="qrp-text-body">
            <span className="qrp-type-pill">Text QR</span>
            <Title level={2}>{textTitle}</Title>
            <Paragraph className="qrp-text-subtitle">Shared text content</Paragraph>
            <div
              className="qrp-text-document qrp-rich-content"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(textContent) }}
            />
          </div>
        </div>
      );
    }

    if (qrType === "File") {
      const fileUrl = qrData.fileUrl || payload.fileUrl;
      const fileName = getFileName(fileUrl);
      const fileExt = getFileExtension(fileUrl);

      return (
        <div className="qrp-file">
          <div className="qrp-file-hero">
            {isImageFile(fileUrl) ? (
              <img src={fileUrl} alt={fileName} />
            ) : (
              <div className="qrp-file-doc"><DownloadOutlined /><span>{fileExt}</span></div>
            )}
          </div>
          <div className="qrp-file-body">
            <span className="qrp-type-pill">File QR</span>
            <Title level={2}>{qrData?.title || "Shared File"}</Title>
            <div className="qrp-file-name">
              <DownloadOutlined />
              <span>{fileName}</span>
              <strong>{fileExt}</strong>
            </div>
            <Button type="primary" size="large" href={fileUrl} target="_blank" disabled={!fileUrl}>
              Open File
            </Button>
          </div>
        </div>
      );
    }

    if (qrType === "vCard") {
      const contactName = payload.name || qrData?.title || "Contact";

      return (
        <div className="qrp-vcard">
          <div className="qrp-vcard-hero">
            <div className="qrp-vcard-avatar">{getInitials(contactName)}</div>
          </div>
          <div className="qrp-vcard-body">
            <span className="qrp-type-pill">vCard QR</span>
            <Title level={2}>{contactName}</Title>
            {payload.designation && <Paragraph className="qrp-vcard-role">{payload.designation}</Paragraph>}
            {payload.companyName && <div className="qrp-vcard-company"><IdcardOutlined /> {payload.companyName}</div>}

            <div className="qrp-vcard-actions">
              {payload.mobile && <a href={`tel:${payload.mobile}`}><PhoneOutlined /> Call</a>}
              {payload.email && <a href={`mailto:${payload.email}`}><MailOutlined /> Email</a>}
              {payload.website && <a href={payload.website} target="_blank" rel="noopener noreferrer"><GlobalOutlined /> Website</a>}
            </div>

            <div className="qrp-contact-card">
              {payload.mobile && <div><strong>Mobile</strong><span>{payload.mobile}</span></div>}
              {payload.email && <div><strong>Email</strong><span>{payload.email}</span></div>}
              {payload.website && <div><strong>Website</strong><span>{payload.website}</span></div>}
              {payload.address && <div><strong>Address</strong><span>{payload.address}</span></div>}
            </div>
          </div>
        </div>
      );
    }

    if (qrType === "Message") {
      const subject = payload.subject || qrData?.title || "Message";
      const description = text || "No message available.";
      const eventDate = payload.eventDate || "";
      const messageImage = getFirstUrl(
        qrData.fileUrl,
        qrData.imageUrl,
        qrData.pictureUrl,
        qrData.attachmentUrl,
        qrData.messageImageUrl,
        qrData.photoUrl,
        qrData.s3Url,
        qrData.file,
        qrData.image,
        qrData.picture,
        qrData.files,
        qrData.attachments,
        qrData.media,
        payload.fileUrl,
        payload.imageUrl,
        payload.pictureUrl,
        payload.attachmentUrl,
        payload.messageImageUrl,
        payload.photoUrl,
        payload.file,
        payload.image,
        payload.picture,
        payload.files
      );

      return (
        <div className="qrp-message-card">
          <div className="qrp-message-hero">
            <div className="qrp-message-icon">
              <MessageOutlined />
            </div>
          </div>
          <div className="qrp-message-body">
            <span className="qrp-type-pill">Message QR</span>
            <Title level={2}>{subject}</Title>
            {eventDate && (
              <div className="qrp-message-date">
                <CalendarOutlined /> {eventDate}
              </div>
            )}
            {messageImage && (
              <div className="qrp-message-picture">
                <img src={messageImage} alt={subject} />
              </div>
            )}
            <div className="qrp-message-note">
              <span>Message</span>
              <div
                className="qrp-rich-content"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="qrp-content">
        <div className="qrp-content-icon"><MessageOutlined /></div>
        <Title level={3}>{payload.subject || (qrType === "Message" ? "Message" : "Text")}</Title>
        {stripHtml(text) ? (
          <div
            className="qrp-message qrp-rich-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
          />
        ) : (
          <Paragraph className="qrp-message">No content available.</Paragraph>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="qrp-page">
        <Card className="qrp-card center"><Spin size="large" /><Paragraph>Loading QR...</Paragraph></Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qrp-page">
        <Card className="qrp-card center">
          <Title level={3}>QR unavailable</Title>
          <Paragraph>{error}</Paragraph>
          <Button icon={<ReloadOutlined />} onClick={fetchQR}>Retry</Button>
        </Card>
      </div>
    );
  }

  if (qrData?.isPasswordProtected && !qrData?.link && !qrData?.qrText && !qrData?.payloadData && !qrData?.fileUrl) {
    return (
      <div className="qrp-page">
        <Card className="qrp-card">
          <div className="qrp-protected">
            <div className={`qrp-protected-visual ${qrData.qrType === "Location" ? "location" : ""} ${qrData.qrType === "Brand" ? "brand" : ""} ${qrData.qrType === "Social" ? "social" : ""} ${qrData.qrType === "File" ? "file" : ""} ${qrData.qrType === "Product" ? "product" : ""}`}>
              {qrData.qrType === "Brand" && qrData.logoUrl ? (
                <img className="brand-logo" src={qrData.logoUrl} alt={qrData.title || "Brand QR"} />
              ) : qrData.qrType === "File" && isImageFile(qrData.fileUrl || parsePayload(qrData.payloadData).fileUrl) ? (
                <img className="file-preview" src={qrData.fileUrl || parsePayload(qrData.payloadData).fileUrl} alt={qrData.title || "File QR"} />
              ) : qrData.qrImageUrl ? (
                <img src={qrData.qrImageUrl} alt={qrData.title || "Protected QR"} />
              ) : (
                <LockOutlined />
              )}
              <div className="qrp-protected-lock"><LockOutlined /></div>
            </div>
            <span className="qrp-type-pill">{qrData.qrType || "QR"} QR</span>
            <Title level={3}>{qrData.title || "Protected QR"}</Title>
            {qrData.qrType === "Brand" && (
              <div className="qrp-protected-brand-note">
                <SafetyCertificateOutlined /> Secure brand link
              </div>
            )}
            {qrData.qrType === "Social" && (
              <div className="qrp-protected-brand-note">
                <ShareAltOutlined /> Protected social link
              </div>
            )}
            {qrData.qrType === "File" && (
              <div className="qrp-protected-brand-note">
                <DownloadOutlined /> Protected file
              </div>
            )}
            <Paragraph className="qrp-muted">This QR is password protected. Enter the password to continue.</Paragraph>
            <div className="qrp-password-form">
              <Input.Password
                size="large"
                placeholder="Enter password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onPressEnter={verifyPassword}
              />
              {passwordError && <div className="qrp-error">{passwordError}</div>}
              <Button type="primary" size="large" block loading={verifying} onClick={verifyPassword}>
                Unlock QR
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return <div className="qrp-page"><Card className="qrp-card">{renderContent()}</Card></div>;
};

export default QRPublic;
